import type {Validations, WhatsappApi, WhatsappState} from 'src/types/whatsapp';
import type {MenuOption} from '@spaced-out/ui-design-system/lib/components/Menu';
import {
  normalizeNodeSchedule,
  parseNodeSchedule,
} from 'src/utils/node-schedule';
import {
  normalizeMessagingNoteWriteback,
  parseMessagingNoteWriteback,
} from 'src/utils/note-writeback';
import {
  normalizeUtmConfig,
  getDefaultUtmConfigState,
} from 'src/utils/utm-config';


export {
  getSenderListWithItsEntityVariables,
  getSenderNameFromVariable,
} from 'src/utils/sms';

export const getSendFromOptions = (
  t: (arg1: string, arg2: string) => string,
): Array<MenuOption> => [
  {label: t('Variable', 'Variable'), key: 'variable'},
  {
    label: t('Choose_Other', 'Choose Other'),
    key: 'choose_other',
  },
  {
    label: t('Default_Phone_Number', 'Default Phone Number'),
    key: 'default_phone_number',
  },
];

export const fallbackSenderPhoneList = [
  {
    key: '0',
    label: 'No Phone Numbers available',
    disabled: true,
  },
] as const;

export const fallbackSenderOption = [
  {
    key: '0',
    label: 'No Variables available',
    disabled: true,
  },
] as const;

export const normalizeWhatsapp = (
  whatsappState: WhatsappState,
): WhatsappApi => {
  const {
    to_number,
    from_number,
    external_source_id,
    selected_template,
    sender_category,
    body,
    variables,
  } = whatsappState;
  const nodeSchedule = normalizeNodeSchedule(whatsappState);
  const noteWriteback = normalizeMessagingNoteWriteback(whatsappState);
  const utmConfig = normalizeUtmConfig(getDefaultUtmConfigState());
  return {
    task: {
      params: {
        to_number,
        from_number,
        external_source_id,
        selected_template,
        sender_category,
        template_id: selected_template?.id,
        body,
        variables,
      },
    },
    ...nodeSchedule,
    ...noteWriteback,
    ...utmConfig,
  };
};

export const getErrors = (validationsObject: Validations): Array<string> => {
  const errors: Array<string> = [];
  const {
    senderNumberEmpty,
    sendToNumberEmpty,
    sameFromToNumber,
    invalidToNumber,
    placeholdersEmpty,
    nwbSentEntityNotSelected,
    nwbSentNoteActionNotSelected,
    nwbReplyEntityNotSelected,
    nwbReplyNoteActionNotSelected,
  } = validationsObject;
  if (senderNumberEmpty || sendToNumberEmpty || placeholdersEmpty) {
    errors.push('Please fill the required fields.');
  }
  if (sameFromToNumber) {
    errors.push('Sender Number cannot be same as Receiver Number.');
  }
  if (invalidToNumber) {
    errors.push('Send To Number is Invalid.');
  }
  if (nwbSentEntityNotSelected) {
    errors.push('Please select an entity for sent note writeback.');
  }
  if (nwbSentNoteActionNotSelected) {
    errors.push('Please select a sent note action.');
  }
  if (nwbReplyEntityNotSelected) {
    errors.push('Please select an entity for reply note writeback.');
  }
  if (nwbReplyNoteActionNotSelected) {
    errors.push('Please select a reply note action.');
  }
  return errors;
};

export const parseWhatsapp = (
  whatsappApiResponse: WhatsappApi,
): WhatsappState => {
  const whatsappSendTask = whatsappApiResponse.task;
  const taskDefinition = whatsappSendTask.params;
  if (!taskDefinition) {
    throw new Error('Whatsapp task definition not found');
  }

  const nodeSchedule = parseNodeSchedule(whatsappApiResponse);
  const noteWriteback = parseMessagingNoteWriteback(whatsappApiResponse);

  return {
    body: taskDefinition.body || '',
    external_source_id: taskDefinition.external_source_id || null,
    from_number: taskDefinition.from_number ?? null,
    to_number: formatToNumber(taskDefinition.to_number),
    sender_category: taskDefinition.sender_category,
    selected_template: taskDefinition.selected_template ?? {id: null},
    variables: taskDefinition.variables ?? null,
    ...nodeSchedule,
    ...noteWriteback,
  };
};
const formatToNumber = (number: string | null | undefined) => {
  if (typeof number !== 'string') {
    return null;
  }

  const isWrappedInBraces = number.startsWith('{{') && number.endsWith('}}');
  return isWrappedInBraces ? number : `{{${number}}}`;
};
